import { IDatasetControlProps, XrmGridType } from "./interfaces";
import { Context } from "@src/ComponentFramework/PropertyClasses/Context";
import { HistoryManager } from "@src/providers/HistoryProvider/HistoryManager";
import { UserSettingsDefinition } from "@src/app/classes/definitions/UserSettingsDefinition";
import { Grid } from "./Grid";
import { DatasetDependencies } from "./DatasetDependencies";
import { IDatasetControl } from '@talxis/base-controls/dist/utils/dataset-control';
import { IFrameFactory } from "@src/app/classes/utilities/IFrameFactory";

export class DatasetControl {
    private _mounted = true;
    private _dataset: Grid;
    private _datasetDependencies: DatasetDependencies;
    private _initialRender = true;
    private _container: HTMLDivElement;
    private _pcfDatasetControlInstance?: IDatasetControl;
    private _pcfContext: ComponentFramework.Context<any, any>;

    constructor(dataset: Grid, dependencies: DatasetDependencies, container: HTMLDivElement) {
        this._dataset = dataset;
        this._container = container;
        this._datasetDependencies = dependencies;
        this._registerActionsAndEvents();
        HistoryManager.activeControls.add(this._datasetDependencies.getControlInstance());
    }

    public async mount() {
        this._mounted = true;
        await this._dataset.getDataProvider().preload();
        await this.render();
        if (!this._pcfDatasetControlInstance) {
            await this._dataset.paging.loadExactPage(this._dataset.paging.pageNumber);
        }

    }
    public getPcfDatasetControlInstance() {
        return this._pcfDatasetControlInstance;
    }
    public unmount() {
        this._mounted = false;
        this._getControlInstance()?.destroy();
        //@ts-ignore - not part of typings
        this._pcfContext.factory.clearAllDOMComponents();
        HistoryManager.activeControls.delete(this._getControlInstance());
        this._dataset.ribbon.destroy();
        if (this._dataset.gridType === XrmGridType.HomepageGrid) {
            IFrameFactory.destroyIFrame(this._dataset.getPageId());
            Xrm.Page = undefined;
        }
    }

    public async render() {
        this._pcfContext = await Context.createDatasetContext(this._dataset, this._getControlProps(), this._datasetDependencies.getControlDefinition(), this._dataset.getStateRef().current);
        if (this._initialRender) {
            this._getControlInstance().init(this._pcfContext, () => this._notifyOutputChanged(), this._dataset.getStateRef().current.getValues('pcf'), this._container);
        }
        if (this._mounted) {
            this._getControlInstance().updateView(this._pcfContext);
        }
        this._initialRender = false;
    }

    public getOutputs() {
        return this._getControlInstance().getOutputs();
    }

    private _registerActionsAndEvents() {
        this._getFormContext()?.registerChildControl(this._getControlProps().id, this._dataset.gridContext);
        this._getFormContext()?.xrmExecutionContext?.formContext?._controlInstanceMap.set(this._getControlProps().id, this._getControlInstance());
        //make refreshing of the homepage grid avaliable
        if (this._dataset.gridType === XrmGridType.HomepageGrid) {
            Xrm.Page = {
                data: {
                    //@ts-ignore - not implementing promise like
                    refresh: () => {
                        this._dataset.refresh();
                    }
                },
                pageId: this._dataset.getPageId()
            };
        }
        this._dataset.addEventListener('onNewDataLoaded', () => this.render());
        this._dataset.addEventListener('onRecordsSelected', ids => this._onRecordsSelected(ids));
        this._dataset.addEventListener('onPageSizeChanged', pageSize => this._onPageSizeChanged(pageSize));
        this._dataset.addEventListener('onRecordColumnValueChanged', () => this._dataset.ribbon.refresh());
        this._dataset.addEventListener('onAfterRecordSaved', () => this._dataset.ribbon.refresh());
    }

    private _onRecordsSelected(ids: string[]) {
        if (ids.length === 1) {
            const selectedRecord = this._dataset.records[ids[0]];
            const executionContext: Xrm.Events.EventContext = {
                //@ts-ignore - We are not implementing entire EventContext, only required items by the scripts.
                getFormContext: () => {
                    return {
                        data: {
                            entity: {
                                getId: () => {
                                    return selectedRecord.getRecordId();
                                },
                                attributes: {
                                    get: (arg1?: any): any => {
                                        if (typeof arg1 === "string") {
                                            return {
                                                getValue: () => selectedRecord.getValue(arg1)
                                            } as Xrm.Attributes.Attribute;
                                        }
                                    }
                                }
                            }
                        }
                    };
                }
            };
            this._getFormContext()?.onRecordSelect(this._getControlProps().id, executionContext);
        }
        if (ids.length > 1) {
            const executionContext: Xrm.Events.EventContext = {
                //@ts-ignore - We are not implementing entire EventContext, only required items by scripts.
                getFormContext: () => {
                    return {
                        getSelectedRows: () => {
                            return ids;
                        }
                    };
                }
            };
            this._getFormContext()?.onRecordsSelect(this._getControlProps().id, executionContext);
        }
        this._getControlBindings()[this._datasetDependencies.getDatasetBindingName()]?.onRecordsSelected(ids);
        this._dataset.ribbon.refresh();
        this.render();
    }

    private _onPageSizeChanged(pageSize: number) {
        if (this._dataset.gridType === XrmGridType.HomepageGrid && this._mounted) {
            UserSettingsDefinition.getUserSettings().setPagingLimit(pageSize);
        }
    }

    private _notifyOutputChanged() {

        this._getFormContext()?.xrmExecutionContext?.formContext._onOutputChangedHandlerMap.get(this._getControlProps().id)?.map(handler => handler({
            ...this._getFormContext().xrmExecutionContext,
            //@ts-ignore - typings
            getFormContext: () => this._dataset.gridContext
        }));
    }

    private _getFormContext() {
        return this._datasetDependencies.getFormContext();
    }
    private _getControlProps(): IDatasetControlProps {
        return {
            ...this._datasetDependencies.getControlProps(),
            childeventlisteners: [{
                eventname: 'onDatasetControlInstanceReady',
                eventhandler: (datasetControl: IDatasetControl) => {
                    this._pcfDatasetControlInstance = datasetControl;
                    this._pcfDatasetControlInstance.addEventListener('onRemountRequested', () => this._dataset.reloadGrid());
                }
            }]
        };
    }
    private _getControlBindings() {
        return this._datasetDependencies.getControlBindings();
    }
    private _getControlInstance() {
        return this._datasetDependencies.getControlInstance();
    }
}